import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import {
  CampaignCard,
  CampaignChannelBadges,
  CampaignReachBar,
} from '#/entities/campaign'
import type { CampaignInsight, CampaignStep } from '#/entities/campaign'
import { cn } from '#/shared/lib/utils'

interface CampaignStepPerformanceProps {
  steps: CampaignStep[]
  /** Null below two sending steps or three bookings — the band then hides. */
  insight: CampaignInsight | null
}

/** The four right-hand figures every step reports. */
function StepEarnings({ step, t }: { step: CampaignStep; t: TFunction }) {
  const cells = [
    {
      key: 'replied',
      value: step.replied,
      label: t('campaigns.detail.step.replied'),
    },
    {
      key: 'booked',
      value: step.booked,
      label: t('campaigns.detail.step.booked'),
    },
    {
      key: 'margin',
      value: step.margin,
      label: t('campaigns.detail.step.margin'),
    },
    {
      key: 'cost',
      value: step.costPerBooking,
      label: t('campaigns.detail.step.costPerBooking'),
      muted: true,
    },
  ]
  return (
    <div className="flex items-center gap-4 sm:gap-6">
      {cells.map((cell) => (
        <div key={cell.key} className="flex flex-col items-end gap-1">
          <p
            className={cn(
              'text-sm font-medium',
              cell.muted ? 'text-gray-500' : 'text-announce-ink',
            )}
          >
            {cell.value}
          </p>
          <p className="text-xs whitespace-nowrap text-gray-300">
            {cell.label}
          </p>
        </div>
      ))}
    </div>
  )
}

function StepRow({ step, t }: { step: CampaignStep; t: TFunction }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-4 py-3.5">
      <div className="flex w-45 shrink-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex size-5 items-center justify-center rounded-full bg-campaign-running text-2xs font-medium text-white"
          >
            {step.position}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {t('campaigns.detail.step.title', { position: step.position })}
          </span>
          <CampaignChannelBadges channels={[step.channel]} />
        </div>
        <p className="text-xs text-gray-300">{step.timing}</p>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 lg:max-w-95">
        <CampaignReachBar
          reach={step.reach}
          tone={step.barTone}
          label={step.delivery}
        />
        <p
          className={cn(
            'text-xs',
            step.pending ? 'text-gray-300' : 'text-gray-500',
          )}
        >
          {step.delivery}
        </p>
      </div>
      <StepEarnings step={step} t={t} />
    </div>
  )
}

function StepExit({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-gray-200 bg-gray-50 py-2 pr-4 pl-12.5">
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 rounded-full bg-campaign-blocked"
      />
      <p className="text-xs text-gray-500">{text}</p>
    </div>
  )
}

export function CampaignStepPerformance({
  steps,
  insight,
}: CampaignStepPerformanceProps) {
  const { t } = useTranslation()
  return (
    <CampaignCard>
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-gray-200 px-4 py-3">
        <h2 className="text-2xs font-medium tracking-wider text-gray-300">
          {t('campaigns.detail.sections.tactic')}
        </h2>
        <p className="text-xs text-gray-300">
          {t('campaigns.detail.step.barLegend')}
        </p>
      </div>
      {steps.map((step) => (
        <div key={step.id}>
          <StepRow step={step} t={t} />
          {step.exit != null && <StepExit text={step.exit} />}
        </div>
      ))}
      {insight != null && (
        <div className="flex items-start gap-2.5 bg-campaign-insight-surface px-4 pt-3 pb-3.5">
          <span
            aria-hidden="true"
            className="mt-1 size-1.5 shrink-0 rounded-full bg-campaign-running"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-xs font-medium text-gray-900">{insight.title}</p>
            <p className="text-xs text-gray-500">{insight.body}</p>
          </div>
        </div>
      )}
    </CampaignCard>
  )
}
